import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import Select from "react-select";
import { useCategoryData } from "../hooks/use-categoryData";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import RuleStateService from "../../services/RuleStateService";
import PPUtils from "../../../utils/PPUtils";
import { formatSentinelNumber } from "../../common/tableQueryUtils";

const resolveComparisonPrice = (row) => {
     const calculated = row.calculatedPrice || {};
     if (calculated.shopPrice != null && calculated.shopPrice !== "") {
          return calculated.shopPrice;
     }
     const foreignShop = row.foreignShopProduct;
     if (foreignShop?.price != null && foreignShop.price !== "") {
          return foreignShop.price;
     }
     if (calculated.shopTotalPrice != null && calculated.shopTotalPrice !== "") {
          return calculated.shopTotalPrice;
     }
     if (foreignShop?.totalPrice != null && foreignShop.totalPrice !== "") {
          return foreignShop.totalPrice;
     }
     return null;
};

const getSelectedValues = (options, keys) => {
     const expandedOptions = (options || []).flatMap((option) =>
          option?.isAllOption && Array.isArray(option.allEntries) ? option.allEntries : [option]
     );

     return [...new Set(
          expandedOptions
               .map((option) => keys.map((key) => option?.[key]).find(Boolean))
               .filter(Boolean)
     )];
};

export default function PPPriceManagementTestRuleModal({
     visible,
     close,
     properties,
     productRuleHelpers,
     foreignShopRuleHelpers,
     actionRuleHelpers
}) {
     const [testData, setTestData] = useState({
          departments: [],
          areas: [],
          categories: [],
          manufacturers: [],
          productNumbers: ""
     });
     const [isRunning, setIsRunning] = useState(false);
     const [results, setResults] = useState([]);
     const [foundCount, setFoundCount] = useState(null);
     const [feedback, setFeedback] = useState(null);

     const {
          departmentOptions,
          areaOptions,
          categoryOptions,
          manufacturerOptions,
          loadingStates,
          loadAreas,
          loadCategories,
          loadManufacturers
     } = useCategoryData();

     const auth = PPUtils.getLogin();
     const token = auth.token;
     const customerId = PPUtils.getEffectiveCustomerId(auth);

     useEffect(() => {
          if (visible) {
               loadManufacturers();
          } else {
               setTestData({
                    departments: [],
                    areas: [],
                    categories: [],
                    manufacturers: [],
                    productNumbers: ""
               });
               setResults([]);
               setFoundCount(null);
               setFeedback(null);
          }
     }, [visible, loadManufacturers]);

     useEffect(() => {
          setFeedback(null);
     }, [testData]);

     const resolveProductIds = async () => {
          const productNumbers = testData.productNumbers?.trim();
          if (productNumbers) {
               const nrs = productNumbers.replace(/\s/g, "").split(",").filter(Boolean);
               return RuleEngineBackend.findProductIdsByProductNrs(token, customerId, "de", nrs);
          }

          const restrictions = {};
          const deptShorts = getSelectedValues(testData.departments, ["catShort"]);
          const areaShorts = getSelectedValues(testData.areas, ["catShort"]);
          const catShorts = getSelectedValues(testData.categories, ["catShort"]);
          const producers = getSelectedValues(testData.manufacturers, ["name", "label", "value"]);

          if (deptShorts.length) restrictions.department = deptShorts;
          if (areaShorts.length) restrictions.product_group = areaShorts;
          if (catShorts.length) restrictions.product_cat = catShorts;
          if (producers.length) restrictions.product_producer = producers;

          return RuleEngineBackend.getProductIdsByCategoryTree(token, customerId, restrictions);
     };

     const handleTest = async () => {
          const hasScope =
               testData.departments.length > 0 ||
               testData.areas.length > 0 ||
               testData.categories.length > 0 ||
               testData.manufacturers.length > 0 ||
               (testData.productNumbers && testData.productNumbers.trim().length > 0);
          if (!hasScope) {
               setFeedback({
                    type: "error",
                    message: "Bitte mindestens einen Filter oder eine Produktnummer auswählen."
               });
               return;
          }

          setIsRunning(true);
          setResults([]);
          setFeedback(null);
          try {
               const productIds = await resolveProductIds();
               if (!Array.isArray(productIds) || productIds.length === 0) {
                    setFeedback({
                         type: "warning",
                         message: "Keine Produkte für die Auswahl gefunden."
                    });
                    setFoundCount(0);
                    return;
               }
               setFoundCount(productIds.length);

               const componentsString = RuleStateService.buildComponentsString(
                    productRuleHelpers.ProductRuleHelper,
                    foreignShopRuleHelpers.ForeignShopRuleHelper,
                    actionRuleHelpers.ActionRuleHelper,
                    properties
               );

               const simulationResults = await RuleEngineBackend.simulateRule(token, {
                    productIds,
                    lang: "de",
                    ruleComponents: componentsString,
                    customerId
               });

               if (simulationResults && Array.isArray(simulationResults)) {
                    setResults(simulationResults);
               } else {
                    setFeedback({
                         type: "error",
                         message: "Simulation fehlgeschlagen."
                    });
               }
          } catch (error) {
               console.error("Rule simulation error:", error);
               const message = error?.message || "Unbekannter Fehler";
               setFeedback({
                    type: "error",
                    message: message.includes("Produktsuche") || message.includes("ids/")
                         ? `Produktsuche fehlgeschlagen: ${message}`
                         : `Fehler bei der Regelsimulation: ${message}`
               });
          } finally {
               setIsRunning(false);
          }
     };

     const selectMenuPortal = {
          menuPortal: (base) => ({ ...base, zIndex: 10000 })
     };

     return (
          <Dialog
               header={
                    <span className="d-flex align-items-center">
                         <i className="fas fa-flask me-2"></i>{' '}Regel testen
                    </span>
               }
               visible={visible}
               style={{ width: "90vw", maxWidth: "1100px" }}
               onHide={close}
               dismissableMask={!isRunning}
               footer={
                    <div className="d-flex justify-content-end gap-2">
                         <button type="button" className="btn btn-secondary" onClick={close} disabled={isRunning}>
                              Abbrechen
                         </button>
                         <button type="button" className="btn btn-primary" onClick={handleTest} disabled={isRunning}>
                              {isRunning ? (
                                   <>
                                        <span className="spinner-border spinner-border-sm me-2" />{' '}Test läuft…
                                   </>
                              ) : (
                                   <>
                                        <i className="fas fa-play me-2"></i>{' '}Test starten
                                   </>
                              )}
                         </button>
                    </div>
               }>
                         {feedback && (
                              <div
                                   className={`alert alert-${feedback.type === "warning" ? "warning" : "danger"} mb-3`}
                                   role="alert"
                              >
                                   {feedback.message}
                              </div>
                         )}

                         <div className="row">
                              <div className="col-12 col-md-6 mb-3">
                                   <label htmlFor="test-rule-department" className="form-label fw-semibold">Abteilung</label>
                                   <Select
                                        inputId="test-rule-department"
                                        options={departmentOptions}
                                        value={testData.departments}
                                        onChange={(selectedOptions) => {
                                             const selectedDepartments = selectedOptions || [];
                                             setTestData(prev => ({
                                             ...prev,
                                             departments: selectedDepartments,
                                             areas: [],
                                             categories: []
                                             }));
                                             if (selectedDepartments.length > 0) {
                                             loadAreas(selectedDepartments);
                                             }
                                        }}
                                        placeholder="Abteilung(en) wählen"
                                        isMulti
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={selectMenuPortal}
                                   />
                              </div>

                              <div className="col-12 col-md-6 mb-3">
                                   <label htmlFor="test-rule-area" className="form-label fw-semibold">Bereich</label>
                                   <Select
                                        inputId="test-rule-area"
                                        options={areaOptions}
                                        value={testData.areas}
                                        onChange={(selectedOptions) => {
                                             const selectedAreas = selectedOptions || [];
                                             setTestData(prev => ({
                                             ...prev,
                                             areas: selectedAreas,
                                             categories: []
                                             }));
                                             if (selectedAreas.length > 0) {
                                             loadCategories(selectedAreas);
                                             }
                                        }}
                                        placeholder="Bereich(e) wählen"
                                        isMulti
                                        isClearable
                                        isDisabled={testData.departments.length === 0}
                                        isLoading={loadingStates.areas}
                                        menuPortalTarget={document.body}
                                        styles={selectMenuPortal}
                                   />
                              </div>

                              <div className="col-12 col-md-6 mb-3">
                                   <label htmlFor="test-rule-category" className="form-label fw-semibold">Kategorie</label>
                                   <Select
                                        inputId="test-rule-category"
                                        options={categoryOptions}
                                        value={testData.categories}
                                        onChange={(selectedOptions) => {
                                             const selectedCategories = selectedOptions || [];
                                             setTestData(prev => ({
                                             ...prev,
                                             categories: selectedCategories
                                             }));
                                        }}
                                        placeholder="Kategorie(n) wählen"
                                        isMulti
                                        isClearable
                                        isDisabled={testData.areas.length === 0}
                                        isLoading={loadingStates.categories}
                                        menuPortalTarget={document.body}
                                        styles={selectMenuPortal}
                                   />
                              </div>

                              <div className="col-12 col-md-6 mb-3">
                                   <label htmlFor="test-rule-manufacturer" className="form-label fw-semibold">Hersteller</label>
                                   <Select
                                        inputId="test-rule-manufacturer"
                                        options={manufacturerOptions}
                                        value={testData.manufacturers}
                                        onChange={(selectedOptions) => {
                                             setTestData(prev => ({
                                             ...prev,
                                             manufacturers: selectedOptions || []
                                             }));
                                        }}
                                        placeholder="Hersteller wählen"
                                        isMulti
                                        isClearable
                                        isLoading={loadingStates.manufacturers}
                                        menuPortalTarget={document.body}
                                        styles={selectMenuPortal}
                                   />
                              </div>

                              <div className="col-12 mb-3">
                                   <label htmlFor="productNumbers" className="form-label fw-semibold">
                                        Produkt-Nr (Komma getrennt)
                                   </label>
                                   <textarea
                                        id="productNumbers"
                                        className="form-control"
                                        rows="3"
                                        value={testData.productNumbers}
                                        onChange={(e) => {
                                             setTestData(prev => ({
                                             ...prev,
                                             productNumbers: e.target.value
                                             }));
                                        }}
                                        placeholder="z.B. 12345, 67890, 11111"
                                   />
                                   <div className="form-text">
                                        Produktnummern haben Vorrang vor Kategorie-Filtern
                                   </div>
                              </div>
                         </div>

                         {foundCount !== null && (
                              <p className="text-muted mb-2">{foundCount} Produkt(e) gefunden</p>
                         )}

                         {results.length > 0 && (
                              <div className="table-responsive mt-3" style={{ maxHeight: "320px" }}>
                                   <table className="table table-sm table-bordered">
                                        <thead className="table-light sticky-top">
                                             <tr>
                                                  <th>Produkt-Nr.</th>
                                                  <th>Produktname</th>
                                                  <th>Preis</th>
                                                  <th>EK</th>
                                                  <th>Vergleichspreis</th>
                                                  <th className="text-nowrap">Neuer Preis</th>
                                                  <th>Kommentar</th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {results.map((row, index) => {
                                                  const calculated = row.calculatedPrice || {};
                                                  const productNr = calculated.fromProductnr || calculated.ean;
                                                  const productName = calculated.productname;
                                                  const currentPrice = calculated.lastPrice;
                                                  const ekPrice = row.ekPrice ?? calculated.ekPrice;
                                                  const comparisonPrice = resolveComparisonPrice(row);
                                                  const newPrice = calculated.price ?? row.nettoNewPrice ?? row.newPrice;
                                                  const comment = row.errorMessage
                                                       || row.failureReason?.reasonText
                                                       || row.comment
                                                       || (row.success ? "OK" : "-");
                                                  return (
                                                       <tr key={index} className={row.success === false ? "table-warning" : ""}>
                                                            <td>{productNr ?? "-"}</td>
                                                            <td>{productName ?? "-"}</td>
                                                            <td>{currentPrice ?? "-"}</td>
                                                            <td>{ekPrice ?? "-"}</td>
                                                            <td>{formatSentinelNumber(comparisonPrice)}</td>
                                                            <td className="text-nowrap">{newPrice ?? "-"}</td>
                                                            <td>{comment}</td>
                                                       </tr>
                                                  );
                                             })}
                                        </tbody>
                                   </table>
                              </div>
                         )}
          </Dialog>
     );
}
